<template>
  <section>
    <header>
      Основное
    </header>

    <VForm ref="form">
      <VInput
        ref="nameInput"
        v-model="localPartner.name"
        label="Название партнера"
        layout="vertical"
        required
        :max-length="300"
      />

      <VFormItem>
        <VInput
          v-model="localPartner.webSiteUrl"
          label="Ссылка на сайт"
          layout="vertical"
        >
          <template #helper-text>
            <a
              :href="localPartner.webSiteUrl || ''"
              target="_blank"
              rel="noopener noreferrer"
            >
              Перейти на сайт
            </a>
          </template>
        </VInput>

        <VLink
          @click="$core.utils.copyToClipboard(localPartner.webSiteUrl)"
        >
          <template #icon>
            <VIcon material="content_copy"/>
          </template>
        </VLink>
      </VFormItem>

      <!-- TODO: выбрать один vselect или чекбоксы и там и там, один вариант -->
      <VSelect
        v-if="!isEditMode"
        ref="typeInput"
        v-model="partnerType"
        label="Тип партнера"
        layout="vertical"
        :options="PartnersService.partnerTypeList"
        :clearable="false"
        required
      />

      <div
        v-else
        style="display: flex; justify-content: space-between; align-items: center;"
      >
        <VCheckbox
          v-model="localPartner.isSeller"
          disabled
        >
          Поставщик
        </VCheckbox>
        <VCheckbox
          v-model="localPartner.isBuyer"
          disabled
        >
          Покупатель
        </VCheckbox>
        <VCheckbox
          v-model="localPartner.isDeliverer"
          disabled
        >
          Транспортная компания
        </VCheckbox>
      </div>

      <VSelect
        v-if="localPartner.isSeller"
        ref="categoryInput"
        v-model="localPartner.category"
        label="Категория"
        required
        :clearable="false"
        layout="vertical"
        :options="PartnerCategoriesService.partnerCategories"
      />

      <VSelect
        ref="countryInput"
        v-model="localPartner.country"
        label="Страна"
        layout="vertical"
        placeholder="Введите название страны"
        data-url="countries.getList"
        option-identity="code"
        option-label="name"
        :minimum-input-length="1"
        required
      />

      <VSelect
        ref="timezoneInput"
        v-model="localPartner.mainTimeZone"
        label="Часовой пояс"
        layout="vertical"
        placeholder="Введите название города"
        :options="timeZoneOptions"
        option-label="stringRepresentation"
        required
      />

      <VSwitch
        v-model="localPartner.isOperationsAllowed"
        theme="slider"
        checked-label="Полный доступ"
        unchecked-label="Тестовый доступ"
      >
        <template #helper-text>
          {{ operationAllowedHelperText }}
        </template>
      </VSwitch>

      <VSelectChips
        ref="responsible"
        v-model="localPartner.responsible"
        label="Ответственные"
        layout="vertical"
        :search-function="PersonsService.searchPersons"
      >
        <template #helper-text>
          Не забудьте сохранить карточку партнера после добавления или удаления ответственного
        </template>
      </VSelectChips>

    </VForm>
    <div>
      <VButton
        v-if="isEditMode"
        theme="primary"
        :disabled="canUpdatePartner"
        @click="savePartner"
      >
        Сохранить
      </VButton>
    </div>
  </section>
</template>

<script lang="ts">

import Partner 						      from "@/domains/Partners/Partner";
import PersonsService 		      from "@/domains/Persons/PersonsService";
import TimeZonesService 	      from "@/domains/TimeZones/TimeZonesService";
import PartnersService 		      from "@/domains/Partners/PartnersService";
import TimeZone                 from "@/domains/TimeZones/TimeZone";
import PartnerCategoriesService from "@/domains/PartnerCategories/PartnerCategoriesService";

import ValidationFormMixin from "@/mixins/validation-form-mixin";

export default defineComponent({
  name: "PartnerInfoSection",

  props: {
    localPartner: {
      type: Partner,
      default: null,
    }
  },

  mixins: [ValidationFormMixin],

  emits: ['partner:updated'],

  data() {
    return {
      PersonsService,
      TimeZonesService,
      PartnersService,
      PartnerCategoriesService,

      /** копия для сравнения и блокировки кнопки удаления */
      initialPartner: this.$core.utils.cloneDeep(this.localPartner),
      timeZoneOptions: [] as TimeZone[]
    }
  },

  async created() {
    this.timeZoneOptions = await TimeZonesService.getList();
  },

  computed: {
    isEditMode() {
      return Boolean(this.localPartner.id);
    },

    /**
     * Тип партнёра для VSelect при создании. Сеттер type в Partner выставляет
     * ровно один из флагов isSeller/isBuyer/isDeliverer, что согласуется с
     * геттером type (взаимоисключающие типы).
     */
    partnerType: {
      get() {
        return PartnersService.partnerTypeList.find(item => item.id === this.localPartner.type);
      },
      set(option: { id: string } | null) {
        this.localPartner.type = option?.id ?? null;
      },
    },

    canUpdatePartner() {
      if (!this.isEditMode) return false;

      return this.$core.utils.isEqual(this.initialPartner, this.localPartner);
    },

    operationAllowedHelperText() {
      if (!this.isEditMode) return 'Создайте партнера для переключения';
      if (!(this.localPartner.companies.length > 0 && this.localPartner.companies[0].bankAccounts.length > 0)) {
        return 'Партнер должен внести данные о юр. лице и банковских счетах для совершения сделок';
      }
      return this.localPartner.isOperationsAllowed
        ? 'Переключите для блокирования доступа к созданию сделок и предложений'
        : 'Переключите чтобы предоставить доступ для совершения сделок и предложений';
    },
  },

  methods: {
    /** Кнопка сохранения есть только у существующего партнёра — создаёт партнёра футер карточки */
    async savePartner() {
      await this.validate();
      if (!this.isValid) {
        this.scrollToError();
      }
      else {
        await this.updatePartner();
      }
    },

    async updatePartner() {
      if (await PartnersService.update(this.localPartner)) {

        const actualPartner = await PartnersService.get(this.localPartner.id);
        Object.assign(this.localPartner, actualPartner);

        this.initialPartner = this.$core.utils.cloneDeep(this.localPartner);
        this.$emit('partner:updated');
      }
    },
  }
})
</script>